<template>
  <div>
    <div class="mt-2 choices-list">
      <ospe-long-term-choice-card
        v-model:choice="longTermApplication.choice"
        title="Training institution and period"
        @change-choice="showSelectExchangeModal"
        @remove-choice="showDeleteExchangePrompt"
      />
    </div>

    <teleport
      to="#vue-portal-target"
      append
    >
      <remote-modal
        ref="selectExchangeModal"
        modal-title="Select exchange"
        modal-class="modal-tabular"
        modal-dialog-class="modal-xl"
        loading-message="Loading exchanges ..."
        :loading-indicator-in-footer="false"
        :hide-content-when-loading="true"
        @details-loaded="onSelectExchangesLoaded"
      >
        <template
          v-if="selectFromExchangesListLoaded"
          v-slot:modal-content
        >
          <div
            v-if="selectFromExchangesList.length > 0"
            class="table-wrapper"
          >
            <table class="table table-hover table-sm">
              <thead>
                <tr>
                  <th>Institution</th>
                  <th>Available places</th>
                  <th>Length</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="exchange of selectFromExchangesList"
                  :key="exchange.id"
                >
                  <td>{{ exchange.institutionName }}</td>
                  <td>{{ exchange.availablePlaces }}</td>
                  <td>{{ exchange.duration }}</td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-sm btn-info"
                      @click.prevent="selectChoice(exchange)"
                    >
                      Select
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-else
            class="alert alert-warning"
          >
            No exchanges found for selected criteria
          </div>
        </template>
        <template v-slot:modal-footer>
          <button
            type="button"
            class="btn btn-sm btn-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>
        </template>
      </remote-modal>
      <modal-alert
        ref="modalAlert"
        close-button-title="Close"
      />
      <modal-confirmation-rails
        ref="removeChoiceConfirmationModal"
        modal-title="Remove choice?"
        @confirmed="onRemoveChoiceConfirmed"
      />
    </teleport>
  </div>
</template>
<script setup lang="ts">
import {onMounted, ref, useTemplateRef, watch} from 'vue'
import Jsona from 'jsona'

import OspeLongTermChoiceCard from './ospe-long-term-choice-card.vue'
import RemoteModal from '../remote-modal.vue'
import ModalAlert from '../modal-alert.vue'
import ModalConfirmationRails from '../modal-confirmation-rails.vue'
import type {OspeActivityType} from '@/js/ospe-public/types'
import type {OspeExchangeChoice, OspeLongTermApplication} from '../../ospe-types'

interface ExchangeData {
  activityType: OspeActivityType;
}

const props = defineProps({
  availableChoicesUrl: {type: String, required: true},
})

const activityType = ref<OspeActivityType>({
  id: 0,
  name: '',
  requiresSpecialisation: false,
  functions: null,
  specialisations: null,
  courtStaffWorkplaces: null,
  otherCountryAllocation: false,
})

const longTermApplication = ref<OspeLongTermApplication>({choice: null})
const selectFromExchangesList = ref<OspeExchangeChoice[]>([])
const selectFromExchangesListLoaded = ref(false)
const applicationDataEl = ref<HTMLInputElement | null>(null)

const selectExchangeModal = useTemplateRef<InstanceType<typeof RemoteModal>>('selectExchangeModal')
const modalAlert = useTemplateRef<InstanceType<typeof ModalAlert>>('modalAlert')
const removeChoiceConfirmationModal = useTemplateRef<InstanceType<typeof ModalConfirmationRails>>('removeChoiceConfirmationModal')

watch(longTermApplication, () => {
  storeApplicationData()
}, {deep: true})

onMounted(() => {
  const exchangesDataEl = document.querySelector('script[ref="exchangeSelectionData"]')
  if (exchangesDataEl instanceof HTMLScriptElement && exchangesDataEl.type == 'application/json') {
    try {
      const exchangeData: ExchangeData = JSON.parse(exchangesDataEl.innerText)
      if (exchangeData.activityType) {
        activityType.value = exchangeData.activityType
      }
    } catch { /* empty */
    }
  }

  const dataEl = document.querySelector('input[ref="applicationDataJson"]')
  if (dataEl instanceof HTMLInputElement) {
    applicationDataEl.value = dataEl
    try {
      longTermApplication.value = JSON.parse(applicationDataEl.value.value)
    } catch { /* empty */
    }
  }
})

const showSelectExchangeModal = (): void => {
  if (activityType.value.id == 0) {
    // no activity type
    return
  }

  selectFromExchangesListLoaded.value = false

  selectExchangeModal.value?.showModal(props.availableChoicesUrl, {
    activity_type_id: activityType.value.id
  })
}

const showDeleteExchangePrompt = (): void => {
  removeChoiceConfirmationModal.value?.show(null, {
    title: 'Remove choice?',
    message: 'Are you sure you want to remove this choice?',
    type: 'danger',
    cancelActionTitle: 'Cancel',
    confirmActionTitle: 'Remove',
    confirmationCode: null
  })
}

const onSelectExchangesLoaded = (data: any): void => {
  selectFromExchangesListLoaded.value = true
  const loadedData = new Jsona().deserialize(data)
  if (Array.isArray(loadedData)) {
    selectFromExchangesList.value = loadedData as OspeExchangeChoice[]
  }
}

const selectChoice = (selectedChoice: OspeExchangeChoice | null): void => {
  selectExchangeModal.value?.hideModal()

  if (longTermApplication.value) {
    if (selectedChoice) {
      longTermApplication.value.choice = prepareChoice(selectedChoice, longTermApplication.value.choice)
    } else {
      longTermApplication.value.choice = null
    }
  }
}

const prepareChoice = (selectedChoice: OspeExchangeChoice, previousChoice: OspeExchangeChoice | null): OspeExchangeChoice => {
  // copy exchange
  const nextChoice = JSON.parse(JSON.stringify(selectedChoice)) as OspeExchangeChoice

  if (previousChoice) {
    if (previousChoice.exchangeTypeId) {
      const typeExists = nextChoice.exchangeTypeItems.some(x => x.id == previousChoice.exchangeTypeId)
      if (typeExists) {
        nextChoice.exchangeTypeId = previousChoice.exchangeTypeId
      }
    }
    if (previousChoice.languageId) {
      const languageExists = nextChoice.languageItems.some(x => x.id == previousChoice.languageId)
      if (languageExists) {
        nextChoice.languageId = previousChoice.languageId
      }
    }
    if (previousChoice.durationId) {
      const durationExists = nextChoice.durationItems.some(x => x.id == previousChoice.durationId)
      if (durationExists) {
        nextChoice.durationId = previousChoice.durationId
      }
    }
    nextChoice.preferredPeriod = previousChoice.preferredPeriod
    nextChoice.specialInterest = previousChoice.specialInterest
  }
  return nextChoice
}

const onRemoveChoiceConfirmed = (): void => {
  removeChoiceConfirmationModal.value?.hide()
  if (longTermApplication.value) {
    longTermApplication.value.choice = null
  }
}

const storeApplicationData = (): void => {
  if (applicationDataEl.value) {
    applicationDataEl.value.value = JSON.stringify(longTermApplication.value)
  }
}
</script>
